import React, { useCallback, useState } from 'react';
import { PDFToolbar } from '../components/toolbar/PDFToolbar';
import { PDFUploadDropzone } from '../components/upload/PDFUploadDropzone';
import { PDFCanvasViewport } from '../components/viewer/PDFCanvasViewport';
import { usePDFDocument } from '../hooks/usePDFDocument';
import { useUploadPDF, useListPDFs } from '../hooks/usePDFApi';
import { useToast } from '@/hooks/use-toast';
import { SCALE_STEP } from '../types';
import { PDFFileList } from '../api';
import { Button } from '@/components/ui/button';
import { FileText, List, X } from 'lucide-react';

interface PDFWorkspaceProps {
  projectId: number;
}

export const PDFWorkspace: React.FC<PDFWorkspaceProps> = ({ projectId }) => {
  const { toast } = useToast();
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedPdfId, setSelectedPdfId] = useState<string | null>(null);

  // Pan position lives here (not in usePDFDocument) to keep it alongside scale.
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Fit-to-screen scale and position remembered for the Reset button.
  const [fitScale, setFitScale] = useState<number>(1);
  const [fitPosition, setFitPosition] = useState({ x: 0, y: 0 });

  const {
    document,
    viewerState,
    isLoading,
    error,
    loadDocument,
    clearDocument,
    setNumPages,
    setCurrentPage,
    setScale,
    setRotation,
  } = usePDFDocument();

  // Combine viewer state with position so the viewport has one clean object.
  const fullViewerState = { ...viewerState, position };

  // API hooks
  const uploadMutation = useUploadPDF();
  const { data: projectPDFs, isLoading: isLoadingPDFs } = useListPDFs(projectId);

  const handleScaleChange = useCallback(
    (nextScale: number) => {
      setScale(Math.max(0.05, Math.min(nextScale, 20)));
    },
    [setScale],
  );

  const handlePositionChange = useCallback(
    (pos: { x: number; y: number }) => {
      setPosition(pos);
    },
    [],
  );

  const handleZoomIn = useCallback(() => {
    handleScaleChange(viewerState.scale + SCALE_STEP);
  }, [handleScaleChange, viewerState.scale]);

  const handleZoomOut = useCallback(() => {
    handleScaleChange(viewerState.scale - SCALE_STEP);
  }, [handleScaleChange, viewerState.scale]);

  const handleRotate = useCallback(() => {
    setRotation(viewerState.rotation + 90);
  }, [setRotation, viewerState.rotation]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage(viewerState.currentPage - 1);
  }, [setCurrentPage, viewerState.currentPage]);

  const handleNextPage = useCallback(() => {
    setCurrentPage(viewerState.currentPage + 1);
  }, [setCurrentPage, viewerState.currentPage]);

  const handleResetView = useCallback(() => {
    setScale(fitScale);
    setPosition(fitPosition);
    setRotation(0);
  }, [setScale, setRotation, fitScale, fitPosition]);

  const handleDownload = useCallback(() => {
    if (!document) return;
    const link = window.document.createElement('a');
    link.href = document.url;
    link.download = document.name;
    link.click();
  }, [document]);

  const resetViewState = useCallback(() => {
    setPosition({ x: 0, y: 0 });
    setFitScale(1);
    setFitPosition({ x: 0, y: 0 });
  }, []);

  // Handle file selection from upload dropzone
  const handleFileSelect = useCallback(async (file: File) => {
    try {
      const pdfFile = await uploadMutation.mutateAsync({ file, projectId });

      toast({
        title: 'Success',
        description: `"${file.name}" uploaded successfully`,
        variant: 'default',
        className: 'bg-green-500 text-white',
      });

      resetViewState();
      loadDocument(file);
      setSelectedPdfId(pdfFile.id);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'Failed to upload PDF',
        variant: 'destructive',
      });
    }
  }, [uploadMutation, projectId, loadDocument, toast, resetViewState]);

  // Handle PDF selection from list
  const handlePDFSelect = useCallback(async (pdf: PDFFileList) => {
    setSelectedPdfId(pdf.id);
    resetViewState();

    try {
      const response = await fetch(pdf.file_url);
      const blob = await response.blob();
      const file = new File([blob], pdf.original_filename, { type: 'application/pdf' });
      loadDocument(file);
    } catch (err) {
      console.error('Error loading PDF:', err);
      toast({
        title: 'Error',
        description: 'Failed to load PDF',
        variant: 'destructive',
      });
    }
  }, [loadDocument, toast, resetViewState]);

  if (!document) {
    return (
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - PDF List */}
        {showSidebar && (
          <div className="w-80 bg-card border-r border-border flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold">Project Drawings</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSidebar(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingPDFs ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-sm text-muted-foreground mt-2">Loading...</p>
                </div>
              ) : projectPDFs && projectPDFs.length > 0 ? (
                <div className="space-y-2">
                  {projectPDFs.map((pdf) => (
                    <button
                      key={pdf.id}
                      className={`w-full p-3 rounded-lg border text-left transition-colors ${
                        selectedPdfId === pdf.id
                          ? 'bg-primary/5 border-primary'
                          : 'bg-muted/30 border-border hover:bg-accent'
                      }`}
                      onClick={() => handlePDFSelect(pdf)}
                    >
                      <div className="flex items-start space-x-2">
                        <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground truncate">
                            {pdf.original_filename}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {pdf.page_count} {pdf.page_count === 1 ? 'page' : 'pages'}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No drawings yet</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Upload your first PDF</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Upload Area */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-2xl">
            {!showSidebar && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSidebar(true)}
                className="mb-4"
              >
                <List className="h-4 w-4 mr-2" />
                Show PDF List
              </Button>
            )}

            <PDFUploadDropzone
              onFileSelect={handleFileSelect}
              isLoading={isLoading || uploadMutation.isPending}
            />

            {error && (
              <div className="mt-4 p-4 bg-destructive/10 border border-destructive/30 rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PDFToolbar
        viewerState={fullViewerState}
        fileName={document.name}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onRotate={handleRotate}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onClose={clearDocument}
        onDownload={handleDownload}
        onResetView={handleResetView}
      />
      <div className="flex-1 overflow-hidden">
        <PDFCanvasViewport
          fileUrl={document.url}
          viewerState={fullViewerState}
          onLoadSuccess={setNumPages}
          onScaleChange={handleScaleChange}
          onPositionChange={handlePositionChange}
          onFitComplete={(scale, pos) => {
            setFitScale(scale);
            setFitPosition(pos);
          }}
        />
      </div>
    </>
  );
};
